import { defineStore } from 'pinia'
import {
  collection,
  query,
  where,
  limit,
  onSnapshot,
  doc,
  getDoc,
} from 'firebase/firestore'
import { db } from '@/firebase'

const DEFAULT_NOTIFICATION_EMAILS = [
  'bpaukan@stmnc.net',
  'alott@stmnc.net',
  'aulak@stmnc.net',
  'staylor@stmnc.net',
]

export const useMeetingStore = defineStore('meeting', {
  state: () => ({
    currentMeeting: null,
    currentBallot: null,
    loading: false,
    error: null,
    _unsubscribe: null,
  }),

  getters: {
    meetingId: (state) => state.currentMeeting?.id || null,
    meetingTitle: (state) => state.currentMeeting?.title || '',
    meetingLocation: (state) => state.currentMeeting?.location || '',
    meetingTime: (state) => state.currentMeeting?.time || '',
    closedDate: (state) => {
      const ts = state.currentMeeting?.closeVoting
      if (!ts) return ''
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    },
    closedTime: (state) => {
      const ts = state.currentMeeting?.closeVoting
      if (!ts) return ''
      const d = ts.toDate ? ts.toDate() : new Date(ts)
      return d.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
      })
    },
    closeVotingDate: (state) => {
      const ts = state.currentMeeting?.closeVoting
      if (!ts) return null
      return ts.toDate ? ts.toDate() : new Date(ts)
    },
    isVotingClosed() {
      const d = this.closeVotingDate
      if (!d) return false
      return new Date() >= d
    },
    meetingLink: (state) => state.currentMeeting?.meetingLink || '',
    showMeetingLink: (state) => !!state.currentMeeting?.showMeetingLink,
    showMeetingVideo: (state) => !!state.currentMeeting?.showMeetingVideo,
    meetingVideoId: (state) => state.currentMeeting?.meetingVideoId || '',
    notificationEmails: (state) =>
      state.currentMeeting?.notificationEmails || DEFAULT_NOTIFICATION_EMAILS,
  },

  actions: {
    subscribe() {
      // Tear down any previous listener before creating a new one. This is
      // important when the caller re-subscribes after an auth change —
      // leaving a stale listener in place leaks a socket and the new auth
      // token never reaches Firestore.
      if (this._unsubscribe) {
        this._unsubscribe()
        this._unsubscribe = null
      }
      this.loading = true
      this.error = null
      const q = query(
        collection(db, 'meetings'),
        where('isCurrent', '==', true),
        limit(1),
      )
      this._unsubscribe = onSnapshot(
        q,
        async (snap) => {
          if (snap.empty) {
            // Helpful breadcrumb when the dashboard shows "no active meeting"
            // unexpectedly — this is almost always either a missing
            // isCurrent=true doc, or a rules-denied read that Firestore
            // surfaces as an empty snapshot on certain SDK versions.
            console.warn(
              '[meetingStore] onSnapshot returned empty — no meetings/{id} doc has isCurrent == true, or the current user lacks read access.',
            )
            this.currentMeeting = null
            this.currentBallot = null
          } else {
            const d = snap.docs[0]
            this.currentMeeting = { id: d.id, ...d.data() }
            if (this.currentMeeting.ballotId) {
              await this.loadBallot(this.currentMeeting.ballotId)
            }
          }
          this.loading = false
        },
        (err) => {
          console.error('[meetingStore] subscribe error', err)
          this.error = err
          this.loading = false
        },
      )
    },

    async loadBallot(ballotId) {
      try {
        const snap = await getDoc(doc(db, 'ballots', ballotId))
        this.currentBallot = snap.exists() ? { id: snap.id, ...snap.data() } : null
      } catch (err) {
        console.error('loadBallot error', err)
        this.currentBallot = null
      }
    },

    async loadMeetingById(meetingId) {
      const snap = await getDoc(doc(db, 'meetings', meetingId))
      if (!snap.exists()) return null
      const meeting = { id: snap.id, ...snap.data() }
      let ballot = null
      if (meeting.ballotId) {
        const bSnap = await getDoc(doc(db, 'ballots', meeting.ballotId))
        if (bSnap.exists()) ballot = { id: bSnap.id, ...bSnap.data() }
      }
      return { meeting, ballot }
    },

    unsubscribe() {
      if (this._unsubscribe) {
        this._unsubscribe()
        this._unsubscribe = null
      }
      this.currentMeeting = null
      this.currentBallot = null
      this.loading = false
      this.error = null
    },
  },
})
